import React, { useEffect, useMemo } from 'react';
import CheckboxInput from '../../../../Components/Form/Inputs/CheckboxInput';
import Table from '../../../../Components/Tables/Table/Table';
import { TableHeader } from '../../../../Components/Tables/Table/TableDefinitions';
import trackingService from '../../../../Services/Tracking/TrackingService';
import styles from './DairyRevenueProtection.module.scss';
import { dairySelectionTableHeaders, DairySelectionTableRow } from './DairyRevenueProtectionDefinitions';

interface IDairyRevenueProtectionSelectionsTableProps {
    selectedTableRows: DairySelectionTableRow[];
    setSelectedTableRows: (value: DairySelectionTableRow[]) => void;
    selectAll: boolean;
    setSelectAll: (selectAll: boolean) => void;
}

const defaultMinimumTableWidth = 900;
const defaultCurrencySignificantDigits = 2;

const DairyRevenueProtectionSelectionsTable: React.FC<IDairyRevenueProtectionSelectionsTableProps> = (props: IDairyRevenueProtectionSelectionsTableProps) => {
    const dairySelectionTableRows = useMemo(() => {
        if (!props.selectedTableRows) {
            return [];
        }

        return props.selectedTableRows!.map((x, index, array) => ({
            ...x,
            actions: (
                <div className={styles.dairy_revenue_protection_checkbox_area}>
                    <CheckboxInput
                        isChecked={!!x.isSelected}
                        handleIsChecked={(isSelected) => {
                            const newRows = array.map((y) => {
                                if (x.effectiveEndDate === y.effectiveEndDate) {
                                    return { ...y, isSelected };
                                }

                                return y;
                            });

                            props.setSelectedTableRows(newRows);

                            trackingService.track({
                                eventCategory: 'InsuranceQuoteEstimator',
                                eventAction: 'DairySelectionsTableSelectionChanged',
                                eventValue: newRows.filter((z) => z.isSelected).map((z) => z.title),
                            });
                        }}
                        track={{
                            eventCategory: 'InsuranceQuoteEstimator',
                            eventAction: 'DairySelectionsTableSelectedChanged',
                        }}
                    />
                </div>
            ),
        }));
    }, [props.selectedTableRows]);

    useEffect(() => {
        props.setSelectAll(props.selectedTableRows.every((x) => x.isSelected));
    }, [props.selectedTableRows]);

    const getCheckboxHeaderCell = () => (
        <div className={styles.dairy_revenue_protection_checkbox_area}>
            <CheckboxInput
                isChecked={props.selectAll}
                handleIsChecked={(isSelected) => {
                    const newRows = dairySelectionTableRows.map((y) => ({ ...y, isSelected }));

                    props.setSelectAll(isSelected);
                    props.setSelectedTableRows(newRows);
                }}
                track={{
                    eventCategory: 'InsuranceQuoteEstimator',
                    eventAction: 'DairySelectionsTableSelectAllChanged',
                    eventValue: props.selectAll,
                }}
            />
        </div>
    );

    const finalHeadersRow = useMemo(() => {
        if (dairySelectionTableRows.length === 0) {
            return dairySelectionTableHeaders;
        }

        return [
            {
                headerValueGetter: getCheckboxHeaderCell,
                field: 'actions',
                elementField: 'actions',
                dataFormat: 'element',
                width: 50,
            },
            ...dairySelectionTableHeaders,
        ] as TableHeader<DairySelectionTableRow>[];
    }, [dairySelectionTableRows, props.selectAll]);

    return (
        <div className={styles.dairy_revenue_protection_coverage_levels_table}>
            <Table
                tableHeaders={finalHeadersRow}
                tableData={dairySelectionTableRows}
                minimumTableWidth={defaultMinimumTableWidth}
                displayDecimalPlaces={defaultCurrencySignificantDigits}
            />
        </div>
    );
};

export default DairyRevenueProtectionSelectionsTable;
